import axios from "axios";

// v2 intentionally ignores the old history-style `compare_items` payload.
// This key only represents the currently open comparison session.
const STORAGE_KEY = 'compare_active_session_v2';
const MAX_ITEMS = 4;

/**
 * Responsive item cap: compare up to 2 products on mobile, 4 on desktop.
 * Breakpoint 768px matches the SCSS mobile boundary (max-width: 767px).
 * MAX_ITEMS (4) stays the hard ceiling and the server-side limit.
 */
function maxForViewport() {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
        return 2;
    }
    return MAX_ITEMS;
}

/**
 * Read the persisted guest/user comparison list from localStorage.
 * Shape: { ids: number[], categoryId: number|null }
 */
function readStorage() {
    try {
        localStorage.removeItem('compare_items');
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return {ids: [], categoryId: null};
        }
        const parsed = JSON.parse(raw);
        return {
            ids: Array.isArray(parsed.ids) ? parsed.ids.filter(Number.isInteger) : [],
            categoryId: parsed.categoryId ?? null,
        };
    } catch (e) {
        return {ids: [], categoryId: null};
    }
}

function writeStorage(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            ids: state.ids,
            categoryId: state.categoryId,
        }));
    } catch (e) {
        // Storage full / disabled — the in-memory state still works for this session.
    }
}

function authHeaders() {
    const token = localStorage.getItem('_token');
    return token ? {headers: {Authorization: 'Bearer ' + token}} : {};
}

export default {
    namespaced: true,
    state() {
        const persisted = readStorage();
        return {
            ids: persisted.ids,
            categoryId: persisted.categoryId,
            max: maxForViewport(),
            // Set true once we've reconciled with the server for a logged-in user.
            synced: false,
        };
    },
    getters: {
        count(state) {
            return state.ids.length;
        },
        isFull(state) {
            return state.ids.length >= state.max;
        },
        has: (state) => (productId) => state.ids.includes(Number(productId)),
        max(state) {
            return state.max;
        },
    },
    mutations: {
        setItems(state, {ids, categoryId}) {
            state.ids = Array.from(new Set((ids || []).map(Number)));
            if (typeof categoryId !== 'undefined') {
                state.categoryId = categoryId;
            }
            if (state.ids.length === 0) {
                state.categoryId = null;
            }
            writeStorage(state);
        },
        addItem(state, {productId, categoryId}) {
            productId = Number(productId);
            if (!state.ids.includes(productId) && state.ids.length < state.max) {
                state.ids.push(productId);
            }
            if (typeof categoryId !== 'undefined' && categoryId !== null) {
                state.categoryId = categoryId;
            }
            writeStorage(state);
        },
        removeItem(state, productId) {
            productId = Number(productId);
            state.ids = state.ids.filter((id) => id !== productId);
            if (state.ids.length === 0) {
                state.categoryId = null;
            }
            writeStorage(state);
        },
        clear(state) {
            state.ids = [];
            state.categoryId = null;
            writeStorage(state);
        },
        setSynced(state, value) {
            state.synced = !!value;
        },
        setMax(state, value) {
            state.max = value;
        },
    },
    actions: {
        /**
         * Recompute the item cap for the current viewport (2 mobile / 4 desktop).
         * Called on boot and on window resize. Never trims an existing list — an
         * over-cap list built on desktop stays intact when viewed on mobile; only
         * further adds are blocked.
         */
        syncMax({commit}) {
            commit('setMax', maxForViewport());
        },

        /**
         * Add a product to the comparison list.
         *
         * Enforces the same-category rule client-side: if the product's category
         * differs from the current list's category, the caller is told via the
         * returned status so it can prompt the user to reset the list.
         *
         * @returns {Promise<{status: string, categoryId?: number}>}
         *   status: 'added' | 'exists' | 'full' | 'category_mismatch'
         */
        async add({state, commit, dispatch}, {productId, categoryId}) {
            productId = Number(productId);
            categoryId = categoryId ?? null;

            if (state.ids.includes(productId)) {
                return {status: 'exists'};
            }

            // Different category than the current list → let the UI decide (confirm reset).
            if (state.ids.length > 0 && state.categoryId !== null
                && categoryId !== null && state.categoryId !== categoryId) {
                return {status: 'category_mismatch', categoryId};
            }

            if (state.ids.length >= state.max) {
                return {status: 'full'};
            }

            commit('addItem', {productId, categoryId});
            const persisted = await dispatch('persist', {productId, mode: 'store'});
            if (!persisted) {
                commit('removeItem', productId);
                return {status: 'error'};
            }
            return {status: 'added'};
        },

        /**
         * Reset the list to a single product of a new category (used when the user
         * confirms switching categories).
         */
        async startFresh({state, commit, dispatch}, {productId, categoryId}) {
            const previous = {ids: [...state.ids], categoryId: state.categoryId};
            commit('clear');
            commit('addItem', {productId, categoryId});
            const persisted = await dispatch('syncToServer');
            if (!persisted) {
                commit('setItems', previous);
                return {status: 'error'};
            }
            return {status: 'added'};
        },

        async remove({state, commit, dispatch}, productId) {
            const previous = {ids: [...state.ids], categoryId: state.categoryId};
            commit('removeItem', productId);
            const persisted = await dispatch('persist', {productId, mode: 'destroy'});
            if (!persisted) {
                commit('setItems', previous);
                return {status: 'error'};
            }
            return {status: 'removed'};
        },

        async clearAll({state, commit, dispatch}) {
            const previous = {ids: [...state.ids], categoryId: state.categoryId};
            commit('clear');
            const persisted = await dispatch('syncToServer');
            if (!persisted) {
                commit('setItems', previous);
                return {status: 'error'};
            }
            return {status: 'cleared'};
        },

        /**
         * Adopt the server-sanitized list. This removes legacy/malformed mixed-category
         * localStorage data and mirrors the clean list to the authenticated account.
         */
        async adoptValidated({commit, dispatch}, {ids, categoryId}) {
            commit('setItems', {ids, categoryId});
            await dispatch('syncToServer');
        },

        /**
         * End the active comparison without restoring it when a server request fails.
         * The local session must disappear immediately when the comparison page closes.
         */
        async endSession({commit, dispatch}) {
            commit('clear');
            await dispatch('syncToServer');
        },

        /**
         * Persist a single add/remove to the server, but only for logged-in users.
         * Guests live entirely in localStorage.
         */
        async persist({rootGetters}, {productId, mode}) {
            if (!rootGetters['user/isLoggedIn']) {
                return true;
            }
            try {
                if (mode === 'store') {
                    await axios.post(`/api/v1/compare/${productId}`, {}, authHeaders());
                } else {
                    await axios.delete(`/api/v1/compare/${productId}`, authHeaders());
                }
                return true;
            } catch (e) {
                return false;
            }
        },

        /**
         * Push the whole local list to the server (used after clear / startFresh).
         */
        async syncToServer({state, rootGetters}) {
            if (!rootGetters['user/isLoggedIn']) {
                return true;
            }
            try {
                await axios.put('/api/v1/compare', {ids: state.ids, replace: true}, authHeaders());
                return true;
            } catch (e) {
                return false;
            }
        },

        /**
         * Comparison is an active browser session, not account history. On login the
         * current local session replaces stale server data instead of merging it.
         */
        async reconcile({state, commit, rootGetters}) {
            if (state.synced || !rootGetters['user/isLoggedIn']) {
                return;
            }
            try {
                const response = await axios.put('/api/v1/compare', {
                    ids: state.ids,
                    replace: true,
                }, authHeaders());
                commit('setItems', {
                    ids: response.data.ids,
                    categoryId: response.data.category_id ?? null,
                });
                commit('setSynced', true);
            } catch (e) {
                // Keep the local list if the server is unreachable.
            }
        },
    },
};
